<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { LanguageType } from '@/__generated__/gateway/graphql';
import DropDown from '@/components/Inputs/DropDown.vue';
import PatternDownloadRetry from '@/shared/components/patterns/PatternDownloadRetry.vue';
import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import IconDownload from '@/shared/components/UI/Icons/IconDownload.vue';
import LoadingSpinner from '@/shared/components/UI/LoadingSpinner.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import { useTranslations } from '@/shared/composables/useTranslations';
import {
  queryPatternBySkuPdf,
  queryPatternLocales
} from '@/shared/graphql/gateway/queries';
import { getApolloClients } from '@/shared/utils/apollo-client';
import { notifyBugsnagWithContext } from '@/shared/utils/error';
import { getLanguageType } from '@/shared/utils/gateway/helpers';

const { t } = useTranslations();

const props = defineProps<{
  active: boolean;
  sku: string;
  contactUuid?: string | null;
}>();

const emit = defineEmits<{
  'update:active': [value: boolean];
  downloaded: [];
}>();

const selectedLanguage = ref<string | null>(null);
const locales = ref<LanguageType[]>([]);
const isLoadingLocales = ref(false);
const isLoadingPdf = ref(false);
const fetchError = ref<string | null>(null);
const downloadStarted = ref(false);

const languageOptions = computed(() =>
  locales.value.map((locale) => ({
    value: locale,
    text: t(`patterns.download_language_modal.languages.${locale}`)
  }))
);

const canDownload = computed(
  () => selectedLanguage.value != null && !isLoadingPdf.value
);

async function fetchLocales(): Promise<void> {
  if (!props.sku) return;
  isLoadingLocales.value = true;
  fetchError.value = null;
  try {
    const { gateway } = await getApolloClients();
    const result = await gateway.query(queryPatternLocales({ sku: props.sku }));
    const list = result.data?.patternLocales?.locales ?? [];
    locales.value = list;
    const currentLocale = getLanguageType(
      window.Shopify?.locale ?? 'en',
      LanguageType.En
    );
    const currentInList = list.includes(currentLocale);
    selectedLanguage.value = (currentInList ? currentLocale : list[0]) ?? null;
  } catch (error) {
    notifyBugsnagWithContext(
      'PatternLanguageModal.fetchLocales',
      error instanceof Error ? error : new Error(String(error)),
      {
        message: 'Failed to fetch pattern locales',
        data: { sku: props.sku }
      }
    );
    fetchError.value = t('patterns.download.error_fetching_url');
    locales.value = [];
    selectedLanguage.value = null;
  } finally {
    isLoadingLocales.value = false;
  }
}

// Each download fetches a fresh signed URL: once one expires, the error
// response is not an attachment and would navigate the shopper off the shop.
async function downloadPdf(): Promise<boolean> {
  if (!props.sku || selectedLanguage.value == null) return false;
  isLoadingPdf.value = true;
  fetchError.value = null;
  try {
    const { gateway } = await getApolloClients();
    const result = await gateway.query(
      queryPatternBySkuPdf(
        props.sku,
        selectedLanguage.value,
        props.contactUuid ?? undefined
      )
    );
    const url = result.data?.patternBySku?.url;
    if (url) {
      window.location.href = url;
      return true;
    } else {
      fetchError.value = t('patterns.download.error_fetching_url');
      notifyBugsnagWithContext(
        'PatternLanguageModal.handleDownload',
        new Error('patternBySku returned no url'),
        {
          message: 'Pattern PDF URL missing',
          data: { sku: props.sku, language: selectedLanguage.value }
        }
      );
    }
  } catch (error) {
    notifyBugsnagWithContext(
      'PatternLanguageModal.handleDownload',
      error instanceof Error ? error : new Error(String(error)),
      {
        message: 'Failed to fetch pattern PDF',
        data: { sku: props.sku, language: selectedLanguage.value }
      }
    );
    fetchError.value = t('patterns.download.error_fetching_url');
  } finally {
    isLoadingPdf.value = false;
  }
  return false;
}

async function handleDownload(): Promise<void> {
  if (await downloadPdf()) {
    downloadStarted.value = true;
    emit('downloaded');
  }
}

watch(
  () => [props.active, props.sku] as const,
  ([active, sku]) => {
    if (active && sku) {
      downloadStarted.value = false;
      fetchLocales();
    } else if (!active) {
      fetchError.value = null;
    }
  },
  { immediate: true }
);
</script>

<template>
  <Modal
    :active="active"
    :size="'md'"
    @update:active="emit('update:active', $event)"
  >
    <template #body>
      <div class="relative flex min-h-[120px] flex-col gap-4 text-black">
        <LoadingSpinner :show="isLoadingLocales" />

        <h2 class="text-2xl font-bold leading-tight">
          {{
            downloadStarted
              ? t('patterns.download.success_title')
              : t('patterns.download_language_modal.title')
          }}
        </h2>

        <PatternDownloadRetry
          v-if="downloadStarted"
          :sku="sku"
          :loading="isLoadingPdf"
          :error="fetchError"
          @retry="downloadPdf"
        />

        <template v-else-if="!isLoadingLocales && languageOptions.length > 0">
          <DropDown
            v-model="selectedLanguage"
            :options="languageOptions"
            :allow-empty="false"
          >
            <template #label>
              {{ t('patterns.download_language_modal.available_languages') }}
            </template>
          </DropDown>

          <p
            v-if="fetchError"
            class="text-xs font-semibold text-error"
            role="alert"
          >
            {{ fetchError }}
          </p>

          <ButtonText
            theme="primary"
            size="lg"
            full-width
            class="justify-center"
            :label="t('patterns.download_language_modal.download')"
            :disabled="!canDownload"
            :loading="isLoadingPdf"
            @click="handleDownload"
          >
            <template #icon>
              <IconDownload class="size-5 shrink-0" aria-hidden="true" />
            </template>
          </ButtonText>
        </template>

        <p
          v-else-if="!isLoadingLocales && languageOptions.length === 0"
          class="text-sm text-black-500"
          role="alert"
        >
          {{ fetchError ?? t('patterns.download.error_fetching_url') }}
        </p>
      </div>
    </template>
  </Modal>
</template>
